import * as Sentry from '@sentry/nextjs';
import { openPage } from '../../nativeTools';
import { ResponseError } from '../types';
import isBrowser from '../../is-browser';
import { getCaptchaUrl } from '../utils';

// 定义错误类型常量
const ErrorType = {
  TIMEOUT: 'timeout_error',
  HTTP_ERROR: 'http_error',
  NETWORK_ERROR: 'network_error',
  UNKNOWN_ERROR: 'unknown_error',
  AUTH_ERROR: 'auth_error',
  RATE_LIMIT: 'rate_limit_error',
} as const;

type ErrorTypes = (typeof ErrorType)[keyof typeof ErrorType];

// 统一错误信息接口
interface ErrorReportInfo {
  hrefReq: string;
  statusCode?: number;
  responseText: string;
  errorMessage: string;
  errorCode?: string;
  requestUrl?: string;
  requestMethod?: string;
  timestamp: string;
  errorType: ErrorTypes;
}

export default function responseError(error: ResponseError): Promise<never> {
  if (!isBrowser) {
    return Promise.reject(error);
  }

  const errorInfo = buildErrorInfo(error);

  if (isTimeoutError(error)) {
    reportToSentry(error, errorInfo);
    console.error('Request timed out:', errorInfo);
  } else if (isHttpError(error)) {
    handleHttpError(error, errorInfo);
  } else {
    errorInfo.errorType = ErrorType.UNKNOWN_ERROR;
    reportToSentry(error, errorInfo);
  }

  return Promise.reject(error);
}

function isTimeoutError(error: ResponseError): boolean {
  return (
    error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout') || error.message?.includes('超时')
  );
}

function isHttpError(error: ResponseError): boolean {
  const errorCode = parseInt(error.code, 10);
  const statusCode = error.response?.status;
  return errorCode >= 400 || (statusCode && statusCode >= 400);
}

function handleHttpError(error: ResponseError, errorInfo: ErrorReportInfo): void {
  const statusCode = error.response?.status;

  if (!window.MaiMai_Native && [403, 401].includes(statusCode!)) {
    const currentUrlStr = encodeURIComponent(window.location.href);
    openPage(`https://maimai.cn/platform/login?to=${currentUrlStr}`);
    return;
  }

  if (statusCode === 429 && error.response?.data?.block_info) {
    const captchaUrl = getCaptchaUrl(error.response.data.block_info);
    openPage(captchaUrl);
    return;
  }

  if (![403, 401, 429].includes(statusCode!)) {
    reportToSentry(error, errorInfo);

    if (process.env.NODE_ENV === 'development') {
      console.error('HTTP Error captured:', errorInfo);
    }
  }
}

function reportToSentry(error: ResponseError, errorInfo: ErrorReportInfo): void {
  // 构建自定义错误标题格式：Client Http Error：[GET] /api/xxx Error
  const method = (errorInfo.requestMethod || error.config?.method || 'UNKNOWN').toUpperCase();
  const status = errorInfo.statusCode || error.response?.status || 'UNKNOWN';
  const url = errorInfo.requestUrl || error.config?.url || '';

  // 安全提取路径（去掉域名和查询参数）
  let path = url;
  try {
    if (url && url.startsWith('http')) {
      path = new URL(url).pathname;
    } else if (url) {
      // 相对路径，直接去掉查询参数
      path = url.split('?')[0];
    }
  } catch (e) {
    // 解析失败，使用原始URL
  }

  const customTitle = `Client Http Error：[${method}] | ${status} | ${path} | ${errorInfo.errorType}`;
  console.info('reportToSentry: ===>', customTitle);
  // 创建新错误对象用于上报（保留原始堆栈）
  const reportingError = new Error(customTitle);
  if (error.stack) {
    reportingError.stack = error.stack;
  }

  Sentry.withScope((scope) => {
    scope.setContext('response_error', {
      href: errorInfo.hrefReq,
      status_code: errorInfo.statusCode,
      response_text: errorInfo.responseText,
      error_message: errorInfo.errorMessage,
      error_code: errorInfo.errorCode,
      request_url: errorInfo.requestUrl,
      request_method: errorInfo.requestMethod,
    });

    scope.setTag('error_type', errorInfo.errorType);
    scope.setTag('status_code', status);
    scope.setTag('http_status', status);
    scope.setTag('from', 'response_error_middleware');
    scope.setTag('http_method', method);

    // 上报自定义标题的错误，并保留原始信息
    Sentry.captureException(reportingError, {
      extra: {
        ...errorInfo,
        originalError: {
          message: error.message,
          stack: error.stack,
          code: error.code,
        },
      },
    });
  });
}

function buildErrorInfo(error: ResponseError): ErrorReportInfo {
  if (isTimeoutError(error)) {
    return {
      hrefReq: window.location.href,
      statusCode: error.response?.status,
      responseText: getResponseText(error),
      errorMessage: error.message || 'Request timeout',
      errorCode: error.code,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      timestamp: new Date().toISOString(),
      errorType: ErrorType.TIMEOUT,
    };
  }

  if (isHttpError(error)) {
    return {
      hrefReq: window.location.href,
      statusCode: error.response?.status,
      responseText: getResponseText(error),
      errorMessage: error.message,
      errorCode: error.code,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      timestamp: new Date().toISOString(),
      errorType: ErrorType.HTTP_ERROR,
    };
  }

  return {
    hrefReq: window.location.href,
    statusCode: error.response?.status,
    responseText: getResponseText(error),
    errorMessage: error.message,
    errorCode: error.code,
    requestUrl: error.config?.url,
    requestMethod: error.config?.method,
    timestamp: new Date().toISOString(),
    errorType: ErrorType.NETWORK_ERROR,
  };
}

function getResponseText(error: ResponseError): string {
  try {
    if (typeof error.response?.data === 'string') {
      return error.response.data;
    } else if (error.response?.data) {
      return JSON.stringify(error.response.data, null, 2);
    } else if (error.response?.statusText) {
      return error.response.statusText;
    } else if (error.message) {
      return error.message;
    }
    return 'No response text available';
  } catch (stringifyError) {
    return 'Failed to stringify response data';
  }
}
